// utils/contentstack.ts (Next.js ready)
import { Config, Region, LivePreview, Stack } from "contentstack";
import * as cheerio from 'cheerio';
import { useEffect, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";

// Basic environment validation
export const isBasicConfigValid = () => {
  return (
    !!process.env.CONTENTSTACK_API_KEY &&
    !!process.env.CONTENTSTACK_DELIVERY_TOKEN &&
    !!process.env.CONTENTSTACK_ENVIRONMENT &&
    !!process.env.CONTENTSTACK_MANAGEMENT_TOKEN &&
    !!process.env.CONTENTSTACK_BRAND
  );
};

export const brandPrimary500Color = {
  dynacast: '#2273BA',
  signicast: '#D9272E',
  optimim: '#5B5297',
  techmire: '#2273BA',
  form_technologies: '#00A19B',
  cirex: '#e9290d',
};

export type BrandName = keyof typeof brandPrimary500Color;

export const currentBrandPrimary500 =
  brandPrimary500Color[(process.env.CONTENTSTACK_BRAND as BrandName) ?? 'dynacast'];

export const getMapIcon = (): string => {
  switch (process.env.NEXT_PUBLIC_CONTENTSTACK_BRAND?.toLocaleLowerCase()) {
    case 'dynacast':
      return '/cms-images/v3/assets/bltc3d459c709c6e7a5/blt86e57202f5a185e5/Dynacast Location.svg';
    case 'signicast':
      return '/cms-images/v3/assets/bltc3d459c709c6e7a5/bltdbac2a3ac30dd3eb/Signicast Location.svg';
    case 'optimim':
      return '/cms-images/v3/assets/bltc3d459c709c6e7a5/bltca510684925a5ce3/Optimim Location.svg';
    case 'techmire':
      return '/cms-images/v3/assets/bltc3d459c709c6e7a5/blt8f52a266dea4aa7d/Techmire Location.svg';
    case 'cirex':
      return '/cms-images/v3/assets/bltc3d459c709c6e7a5/bltf2b01bbf855af8c5/Form Location.svg';
    case 'form_technologies':
      return '/cms-images/v3/assets/bltc3d459c709c6e7a5/bltf2b01bbf855af8c5/Form Location.svg';
    default:
      return '/cms-images/v3/assets/bltc3d459c709c6e7a5/bltf2b01bbf855af8c5/Form Location.svg';
  }
};

// Maps a site locale (e.g. "zh-tw") to the language/region codes expected by the
// Google Maps JS API. Google localizes the map's own labels and UI controls from
// these. Most locales use the base language subtag; Chinese and en-GB need the
// region suffix, and Mexico uses Latin-American Spanish.
const GOOGLE_MAP_LOCALE: Record<string, { language: string; region?: string }> = {
  'en-us': { language: 'en', region: 'US' },
  'en-gb': { language: 'en-GB', region: 'GB' },
  'en-sg': { language: 'en', region: 'SG' },
  'es-mx': { language: 'es-419', region: 'MX' },
  'es-es': { language: 'es', region: 'ES' },
  'fr-fr': { language: 'fr', region: 'FR' },
  'de-de': { language: 'de', region: 'DE' },
  'hi-in': { language: 'hi', region: 'IN' },
  'it-it': { language: 'it', region: 'IT' },
  'ja-jp': { language: 'ja', region: 'JP' },
  'ko-kr': { language: 'ko', region: 'KR' },
  'zh-cn': { language: 'zh-CN', region: 'CN' },
  'zh-tw': { language: 'zh-TW', region: 'TW' },
  'vi-vn': { language: 'vi', region: 'VN' },
};

export const getGoogleMapLocale = (
  locale?: string | null,
): { language: string; region?: string } => {
  const mapped = locale ? GOOGLE_MAP_LOCALE[locale.toLowerCase()] : undefined;
  return mapped ?? { language: 'en', region: 'US' };
};

// The Google Maps JS API can only be loaded once per page session, in a single
// language. @googlemaps/js-api-loader throws "Loader must not be called again
// with different options" if a second Loader is constructed with different
// options, and Google's own bootstrap caches window.google.maps.importLibrary —
// so simply removing the script tag is not enough to reload in a new language.
//
// We share ONE Loader across every map on the site. When the requested language
// changes (client-side locale switch), we fully tear the API down — remove the
// injected script, delete window.google so the bootstrap re-runs, and clear the
// loader singleton — then build a fresh Loader so the map reloads in the new
// language. Callers must re-run their map init after calling this (i.e. depend
// on `locale`).
const GOOGLE_MAPS_SCRIPT_ID = '__googleMapsScriptId';

let mapsLoader: Loader | null = null;
let mapsLoaderLanguage: string | null = null;

// Tears down the Google Maps JS API so it can be reloaded in a different
// language. Safe to call on the server (no-ops) and when nothing is loaded yet.
export const resetGoogleMaps = (): void => {
  if (typeof window !== 'undefined') {
    document.getElementById(GOOGLE_MAPS_SCRIPT_ID)?.remove();
    document
      .querySelectorAll('script[src*="maps.googleapis.com"]')
      .forEach(script => script.remove());
    // Removing window.google forces Google's bootstrap to re-run on next load,
    // picking up the new language/region query params.
    delete (window as unknown as { google?: unknown }).google;
  }
  // Clear the js-api-loader singleton so a new Loader with different options is
  // permitted instead of throwing.
  (Loader as unknown as { instance?: Loader }).instance = undefined;
  mapsLoader = null;
  mapsLoaderLanguage = null;
};

export const getGoogleMapsLoader = (apiKey: string, locale?: string | null): Loader => {
  const { language, region } = getGoogleMapLocale(locale);

  // Language switched since the API loaded -> rebuild it in the new language.
  if (mapsLoader && mapsLoaderLanguage !== language) {
    resetGoogleMaps();
  }

  if (!mapsLoader) {
    mapsLoader = new Loader({ apiKey, version: 'weekly', language, region });
    mapsLoaderLanguage = language;
  }

  return mapsLoader;
};

export function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounced(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debounced;
}

export const generateUrlBasedOnRegion = (): string[] => {
  return Object.keys(Region).map(region =>
    region === 'US' ? `cdn.contentstack.io` : `${region}-cdn.contentstack.com`,
  );
};

export const isValidCustomHostUrl = (url: string): boolean => {
  return url ? !generateUrlBasedOnRegion().includes(url) : false;
};

export const generateVideoThumbnail = async (videoUrl: string): Promise<string> => {
  if (typeof window === 'undefined') return ''; // SSR guard

  return new Promise<string>((resolve, reject) => {
    const video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';

    video.addEventListener('loadeddata', () => {
      video.currentTime = 1;
    });

    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        resolve(canvas.toDataURL());
      } else {
        reject(new Error('Unable to get canvas context'));
      }
    });

    video.addEventListener('error', () => reject(new Error('Error loading video')));
  });
};

export const extractWistiaJsonpUrl = (input: string) => {
  const match = input.match(
    /https:\/\/fast\.wistia\.com\/embed\/medias\/\w+\.jsonp/
  );
  return match ? match[0] : null;
};

export const generateWistiaThumbnail = async (
  html: string
): Promise<string> => {
  const jsonLink = extractWistiaJsonpUrl(html);
  if (!jsonLink) throw new Error('Invalid Wistia embed');

  const res = await fetch(jsonLink);
  const text = await res.text();
  const match = text.match(/({.*})/);
  if (!match) throw new Error('Unable to parse JSONP');

  const json = JSON.parse(match[0]);
  const asset =
    json?.media?.assets?.find((a: any) => a?.type === 'original') || json?.media?.assets?.[0];
  const previewTime = Math.round((json?.media?.duration || 0) / 2);
  if (asset?.url) {
    return asset.url.replace('.bin', '.jpg') + `?video_still_time=${previewTime}`;
  }

  throw new Error('No valid video asset found');
};

export const handleSwiperBreakpoint = (swiper: any, setSlidesPerView: (val: number) => void) => {
  if (swiper.params.slidesPerView === 'auto') {
    setSlidesPerView(swiper.slidesPerViewDynamic());
  } else {
    setSlidesPerView(swiper.params.slidesPerView as number);
  }
};

export const setLocalStorageItem = (key: string, value: unknown) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('Error setting localStorage:', e);
  }
};

export const getLocalStorageItem = (key: string) => {
  if (typeof window === 'undefined') return null;
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : null;
  } catch (e) {
    console.error('Error reading localStorage:', e);
    return null;
  }
};

export const removeLocalStorageItem = (key: string) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error('Error removing localStorage item:', e);
  }
};

export const scrollToTopSmooth = () => {
  if (typeof window === 'undefined') return;
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const isBadHref = (v?: string | null) =>
  !v || v.trim() === '' || v.trim() === '#' || /^javascript:/i.test(v)

export function cleanHtml(html: string): string {
  if (typeof window === 'undefined') {
    const $ = cheerio.load(html)

    $('img').each((_, img) => {
      const $img = $(img)
      const src = $img.attr('src') || ''
      const assetUid = $img.attr('asset_uid')
      $img.attr('src', src.replace(/["“”]/g, ''))
      if (assetUid) {
        $img.attr('data-asset-uid', assetUid.replace(/["“”]/g, ''))
        $img.removeAttr('asset_uid')
      }
      const width = $img.attr('width')
      const height = $img.attr('height')
      if (width) $img.attr('width', width.replace(/["“”]/g, ''))
      if (height) $img.attr('height', height.replace(/["“”]/g, ''))
    })

    $('figure').each((_, figure) => {
      const $figure = $(figure)
      const img = $figure.find('img').first()
      if (img.length) $figure.replaceWith(img)
    })

    $('p').each((_, p) => {
      if (!$(p).text().trim()) $(p).remove()
    })

    $('div[data-img-caption-container]').removeAttr('data-img-caption-container')

    // New anchor cleanup
    $('a').each((_, a) => {
      const $a = $(a)
      const href = $a.attr('href') || ''
      if (isBadHref(href)) {
        $a.replaceWith($a.text() || '')
        return
      }
      // strip smart quotes and spaces
      $a.attr('href', href.replace(/["“”]/g, '').trim())
      // optional, drop target without valid href
      if (!$a.attr('href')) $a.replaceWith($a.text() || '')
    })

    return $('body').html() || ''
  } else {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    doc.querySelectorAll('img').forEach(img => {
      const src = img.getAttribute('src') || ''
      img.setAttribute('src', src.replace(/["“”]/g, ''))
      const assetUid = img.getAttribute('asset_uid')
      if (assetUid) {
        img.setAttribute('data-asset-uid', assetUid.replace(/["“”]/g, ''))
        img.removeAttribute('asset_uid')
      }
      if (img.getAttribute('width')) {
        img.setAttribute('width', (img.getAttribute('width') || '').replace(/["“”]/g, ''))
      }
      if (img.getAttribute('height')) {
        img.setAttribute('height', (img.getAttribute('height') || '').replace(/["“”]/g, ''))
      }
    })

    doc.querySelectorAll('figure').forEach(figure => {
      const img = figure.querySelector('img')
      if (img) figure.replaceWith(img)
    })

    doc.querySelectorAll('p').forEach(p => {
      if (!p.textContent?.trim()) p.remove()
    })

    doc.querySelectorAll('div[data-img-caption-container]').forEach(div => {
      div.removeAttribute('data-img-caption-container')
    })

    // New anchor cleanup
    doc.querySelectorAll('a').forEach(a => {
      const href = a.getAttribute('href') || ''
      if (isBadHref(href)) {
        const span = doc.createElement('span')
        span.textContent = a.textContent || ''
        a.replaceWith(span)
      } else {
        a.setAttribute('href', href.replace(/["“”]/g, '').trim())
        if (!a.getAttribute('href')) {
          const span = doc.createElement('span')
          span.textContent = a.textContent || ''
          a.replaceWith(span)
        }
      }
    })

    return doc.body.innerHTML
  }
}
